import { useState, useMemo, useRef, useEffect } from "react"
import { router } from "@inertiajs/react"
import { FolderOpen, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { FolderPicker } from "./FolderPicker"

type CaptureMode = "voice" | "audio" | "editor" | "file"

interface FrontMatterField {
  id: number
  name: string
  options: Array<{ id: number; label: string }>
}

interface SaveFlowScreenProps {
  captureMode: CaptureMode
  recordingData?: {
    key: string | null
    uploadId: string | null
    parts: Array<{ etag: string; part_number: number }>
    elapsed?: number
    fileName?: string
  }
  selectedFile?: File | null
  frontMatterFields: FrontMatterField[]
  defaultDestination: string
  rootFolder: string
  fileNameTemplate: string
  routingRules: Array<{
    front_matter_field_id: number
    front_matter_option_id: number
    destination_folder: string
  }>
  onSaved: () => void
  onCancel: () => void
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) return "th"
  switch (day % 10) {
    case 1: return "st"
    case 2: return "nd"
    case 3: return "rd"
    default: return "th"
  }
}

function formatAudioLine(elapsedSeconds: number): string {
  const now = new Date()

  const hours = Math.floor(elapsedSeconds / 3600)
  const minutes = Math.floor((elapsedSeconds % 3600) / 60)
  const seconds = elapsedSeconds % 60
  const duration = hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
    : `${minutes}:${String(seconds).padStart(2, "0")}`

  const dayName = now.toLocaleDateString("en-US", { weekday: "long" })
  const month = now.toLocaleDateString("en-US", { month: "short" })
  const day = now.getDate()
  const daySuffix = getDaySuffix(day)

  const timeStr = now.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  })

  return `_Audio recording (${duration}) made on ${dayName}, ${month} ${day}${daySuffix} @ ${timeStr}._`
}

export function SaveFlowScreen({
  captureMode,
  recordingData,
  selectedFile,
  frontMatterFields,
  defaultDestination,
  rootFolder,
  fileNameTemplate,
  routingRules,
  onSaved,
  onCancel,
}: SaveFlowScreenProps) {
  // Pre-fill input with uploaded file name (without extension) for file uploads
  const initialTitle = useMemo(() => {
    if (captureMode === "file" && selectedFile) {
      const name = selectedFile.name
      const dotIndex = name.lastIndexOf(".")
      return dotIndex > 0 ? name.substring(0, dotIndex) : name
    }
    return ""
  }, [captureMode, selectedFile])

  const [input, setInput] = useState(initialTitle)
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number>>({})
  const [destination, setDestination] = useState(defaultDestination)
  const [showFolderPicker, setShowFolderPicker] = useState(false)
  const [saving, setSaving] = useState(false)
  const titleInputRef = useRef<HTMLInputElement>(null)

  // Auto-focus and select the title input for file uploads
  useEffect(() => {
    if (captureMode === "file" && selectedFile && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [captureMode, selectedFile])

  // Compute file name from template
  const fileName = useMemo(() => {
    const now = new Date()
    return fileNameTemplate
      .replace("{YYYY}", String(now.getFullYear()))
      .replace("{MM}", String(now.getMonth() + 1).padStart(2, "0"))
      .replace("{DD}", String(now.getDate()).padStart(2, "0"))
      .replace("{HH}", String(now.getHours()).padStart(2, "0"))
      .replace("{mm}", String(now.getMinutes()).padStart(2, "0"))
      .replace("{input}", input || "untitled")
  }, [input, fileNameTemplate])

  // Auto-destination from routing rules
  function handleOptionSelect(fieldId: number, optionId: number) {
    const newSelected = { ...selectedOptions }

    if (newSelected[fieldId] === optionId) {
      delete newSelected[fieldId]
    } else {
      newSelected[fieldId] = optionId
    }

    setSelectedOptions(newSelected)

    // Check routing rules
    const matchingRule = routingRules.find(
      (r) => r.front_matter_field_id === fieldId && r.front_matter_option_id === optionId
    )
    if (matchingRule) {
      setDestination(matchingRule.destination_folder)
    }
  }

  async function handleSave() {
    setSaving(true)

    try {
      // Build front matter from selected options
      const frontMatter: Record<string, string> = {}
      for (const field of frontMatterFields) {
        const selectedOptId = selectedOptions[field.id]
        if (selectedOptId) {
          const option = field.options.find((o) => o.id === selectedOptId)
          if (option) {
            frontMatter[field.name] = option.label
          }
        }
      }

      if (captureMode === "file" && selectedFile) {
        // File/image upload: send binary to Dropbox via multipart upload
        const ext = selectedFile.name.includes(".")
          ? selectedFile.name.substring(selectedFile.name.lastIndexOf("."))
          : ""
        const formData = new FormData()
        formData.append("file", selectedFile)
        formData.append("file_name", fileName + ext)
        formData.append("destination_path", destination)

        await fetch("/api/file_uploads", {
          method: "POST",
          headers: { "X-CSRF-Token": getCSRFToken() },
          body: formData,
        })

        onSaved()
        return
      } else if (recordingData?.key) {
        // Voice recording: create file first, then finalize, then navigate to editor
        const audioLine = formatAudioLine(recordingData.elapsed || 0)
        const body = `${audioLine}\n`

        // Step 1: Create the markdown file in Dropbox
        const createRes = await fetch("/api/files", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCSRFToken(),
          },
          body: JSON.stringify({
            file_name: fileName,
            destination_path: destination,
            front_matter: { ...frontMatter, source: "voice" },
            body,
          }),
        })
        const createData = await createRes.json()
        const filePath = createData.path as string

        // Step 2: Finalize recording (uploads to R2, starts transcription)
        const finalizeRes = await fetch("/api/recordings/finalize", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCSRFToken(),
          },
          body: JSON.stringify({
            key: recordingData.key,
            file_name: fileName,
            destination_path: destination,
            file_path: filePath,
          }),
        })
        const finalizeData = await finalizeRes.json()
        const transcriptionJobId = finalizeData.transcription_job.id

        // Step 3: Navigate to the editor with transcription context
        const editorPath = filePath.replace(/^\/+/, "")
        const encodedPath = editorPath.split("/").map(segment => encodeURIComponent(segment)).join("/")
        router.visit(`/files/${encodedPath}?transcription_job_id=${transcriptionJobId}`)
        return
      } else {
        // Non-recording save: create markdown file
        await fetch("/api/files", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCSRFToken(),
          },
          body: JSON.stringify({
            file_name: fileName,
            destination_path: destination,
            front_matter: frontMatter,
            body: "",
          }),
        })
      }

      onSaved()
    } catch {
      // error handling
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col items-center justify-center h-full gap-8 p-8" data-testid="save-flow-screen">
      <h2 className="text-xl font-semibold">Start your note</h2>

      {/* File name + destination container */}
      <div className="w-full max-w-md lg:max-w-lg rounded-lg border bg-card p-4 space-y-4">
        <div className="space-y-2">
          <Input
            ref={titleInputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="describe this file..."
            data-testid="file-name-input"
          />
          <p className="text-xs text-muted-foreground" data-testid="file-name-preview">
            {fileName}.md
          </p>
        </div>

        <div className="border-t pt-3">
          <button
            onClick={() => setShowFolderPicker(true)}
            className="flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-accent"
            data-testid="destination-picker"
          >
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
            <span className="flex-1 text-left">{destination}</span>
            <span className="text-xs text-primary font-medium">Change</span>
          </button>
        </div>
      </div>

      {/* Front matter tags container */}
      {frontMatterFields.length > 0 && (
        <div className="w-full max-w-md lg:max-w-lg rounded-lg border bg-card p-4 space-y-4" data-testid="front-matter-tags">
          {frontMatterFields.map((field) => (
            <div key={field.id} className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground capitalize">{field.name}</label>
              <div className="flex flex-wrap gap-2">
                {field.options.map((option) => {
                  const isSelected = selectedOptions[field.id] === option.id
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleOptionSelect(field.id, option.id)}
                      data-testid={`tag-${field.name}-${option.label}`}
                    >
                      <Badge variant={isSelected ? "default" : "outline"}>
                        {option.label}
                      </Badge>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="w-full max-w-md lg:max-w-lg">
        <Button onClick={handleSave} disabled={saving} className="w-full" size="lg" data-testid="save-button">
          {saving ? "Saving..." : "Create"}
        </Button>
      </div>

      <FolderPicker
        open={showFolderPicker}
        onClose={() => setShowFolderPicker(false)}
        onSelect={(path) => {
          setDestination(path)
          setShowFolderPicker(false)
        }}
        currentPath={destination}
        rootPath={rootFolder}
      />
    </div>
  )
}
